import { useCallback, useEffect, useRef, useState } from "react";
import { LogOut } from "lucide-react";
import { api, token, fmtPrice } from "../lib/api";
import Gate from "../components/studio/Gate";
import ArtworkFields from "../components/studio/ArtworkFields";
import BioCard from "../components/studio/BioCard";
import EnquiryInbox from "../components/studio/EnquiryInbox";
import NewsCard from "../components/studio/NewsCard";
import NotificationBar from "../components/studio/NotificationBar";
import SiteTextCard from "../components/studio/SiteTextCard";
import StudioLibrary from "../components/studio/StudioLibrary";
import WorksList from "../components/studio/WorksList";

function Stat({ label, value, testid }) {
  return (
    <div className="border border-line bg-panel p-5 md:p-6" data-testid={testid}>
      <p className="font-meta text-2xl md:text-3xl text-gold">{value}</p>
      <p className="eyebrow mt-2">{label}</p>
    </div>
  );
}

export default function Studio() {
  const [authed, setAuthed] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [settings, setSettings] = useState(null);
  const [library, setLibrary] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [news, setNews] = useState([]);
  const [editing, setEditing] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const seenRef = useRef(null);

  const load = useCallback(async () => {
    const [a, s, l, e, n] = await Promise.all([
      api.get("/artworks"),
      api.get("/settings"),
      api.get("/library"),
      api.get("/enquiries"),
      api.get("/news"),
    ]);
    setArtworks(a.data);
    setSettings(s.data);
    setLibrary(l.data);
    setEnquiries(e.data);
    setNews(n.data);
  }, []);

  const checkNew = useCallback(async () => {
    try {
      const [e, p] = await Promise.all([api.get("/enquiries"), api.get("/payments")]);
      const fresh = [];
      if (!seenRef.current) {
        seenRef.current = {
          enq: new Set(e.data.map((x) => x.id)),
          pay: new Set(p.data.map((x) => x.id)),
        };
        return;
      }
      for (const x of e.data) {
        if (!seenRef.current.enq.has(x.id)) {
          seenRef.current.enq.add(x.id);
          fresh.push({
            id: "e-" + x.id,
            type: "message",
            text: `New message from ${x.name} about “${x.artwork_title}”`,
          });
        }
      }
      for (const x of p.data) {
        if (!seenRef.current.pay.has(x.id)) {
          seenRef.current.pay.add(x.id);
          fresh.push({
            id: "p-" + x.id,
            type: "sale",
            text: `Sold! ${x.artwork_title} — ${fmtPrice(Number(x.amount))}`,
          });
        }
      }
      if (fresh.length) {
        setAlerts((a) => [...fresh, ...a].slice(0, 5));
        if ("Notification" in window && Notification.permission === "granted") {
          for (const f of fresh) {
            try {
              new Notification(f.type === "sale" ? "Sold — A Physical Mind" : "New message — A Physical Mind", {
                body: f.text,
              });
            } catch {}
          }
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!token.get()) return setAuthed(false);
    api
      .get("/studio/check")
      .then(() => setAuthed(true))
      .catch(() => {
        token.clear();
        setAuthed(false);
      });
  }, []);

  useEffect(() => {
    if (authed) load().catch(() => {});
  }, [authed, load]);

  useEffect(() => {
    if (!authed) return;
    checkNew();
    const t = setInterval(checkNew, 30000);
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    return () => clearInterval(t);
  }, [authed, checkNew]);

  if (authed === null)
    return (
      <div className="min-h-screen grid place-items-center" data-testid="studio-loading">
        <p className="eyebrow animate-pulse">Opening the studio…</p>
      </div>
    );

  if (!authed) return <Gate onSuccess={() => setAuthed(true)} />;

  const originals = artworks.filter((a) => a.availability === "original").length;
  const prints = artworks.filter((a) => a.availability === "prints").length;
  const value = artworks.reduce((s, a) => s + (a.price || 0), 0);

  const startEdit = (a) => {
    setEditing(a);
    setTimeout(
      () =>
        document
          .getElementById("artwork-form")
          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
      60
    );
  };

  return (
    <div data-testid="studio-page" className="min-h-screen pt-32 md:pt-40 pb-24">
      <div className="mx-auto max-w-6xl px-6">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="eyebrow !text-ochre/90">Private Studio</p>
            <h1 className="font-display font-light text-4xl md:text-5xl mt-3">
              The Atelier Desk
            </h1>
          </div>
          <button
            onClick={() => {
              token.clear();
              setAuthed(false);
            }}
            data-testid="studio-logout-button"
            className="flex items-center gap-2 font-meta text-[10px] uppercase tracking-[0.25em] text-mute hover:text-bone transition-colors border border-line px-4 py-2"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>

        <div className="mt-8">
          <NotificationBar alerts={alerts} onDismiss={(id) => setAlerts((a) => a.filter((x) => x.id !== id))} />
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Works" value={artworks.length} testid="stat-works" />
          <Stat label="Originals" value={originals} testid="stat-originals" />
          <Stat label="Prints Available" value={prints} testid="stat-prints" />
          <Stat label="Collection Value" value={value ? fmtPrice(value) : "—"} testid="stat-value" />
        </div>

        <div className="mt-12">
          <EnquiryInbox enquiries={enquiries} onChanged={() => load().catch(() => {})} />
        </div>

        <div className="mt-12">
          <StudioLibrary library={library} onChanged={() => load().catch(() => {})} />
        </div>

        <div className="mt-12">
          <SiteTextCard />
        </div>

        <div className="mt-12">
          <NewsCard posts={news} onChanged={() => load().catch(() => {})} />
        </div>

        <div className="mt-12 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start">
          <div className="space-y-10">
            <ArtworkFields
              key={editing?.id || "new"}
              artwork={editing}
              library={library}
              onDone={() => {
                setEditing(null);
                load().catch(() => {});
              }}
              onCancel={() => setEditing(null)}
            />
            <BioCard settings={settings} onSaved={() => load().catch(() => {})} />
          </div>
          <WorksList artworks={artworks} onEdit={startEdit} onDeleted={() => load().catch(() => {})} />
        </div>
      </div>
    </div>
  );
}
